'use client'

import { useInfiniteQuery } from '@tanstack/react-query'
import { useEffect, useMemo, useRef } from 'react'
import blogApi from '~/apis/blog'
import Posts from '~/components/posts'
import { ResBlog } from '~/types/blog'

type HomePostsInfiniteProps = {
    initialBlogs: ResBlog[]
    tag?: string
    limit?: number
}

const HomePostsInfinite = ({ initialBlogs, tag = '', limit = 20 }: HomePostsInfiniteProps) => {
    const loadMoreRef = useRef<HTMLDivElement>(null)

    const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isError } = useInfiniteQuery({
        queryKey: ['home-blogs', tag, limit],
        initialPageParam: 1,
        staleTime: 1000 * 60 * 5,
        refetchOnMount: false,
        refetchOnReconnect: false,
        refetchOnWindowFocus: false,
        queryFn: ({ pageParam }) =>
            blogApi.getBlogs({
                nameTag: tag,
                page: pageParam,
                limit
            }),
        getNextPageParam: (lastPage, allPages) => {
            if (lastPage.length < limit) {
                return undefined
            }
            return allPages.length + 1
        },
        initialData: {
            pages: [initialBlogs],
            pageParams: [1]
        }
    })

    const posts = useMemo(() => data?.pages.flat() || [], [data])

    useEffect(() => {
        const target = loadMoreRef.current
        if (!target || !hasNextPage) return

        const observer = new IntersectionObserver(
            (entries) => {
                const [entry] = entries
                if (entry?.isIntersecting && !isFetchingNextPage) {
                    fetchNextPage()
                }
            },
            {
                rootMargin: '240px 0px'
            }
        )

        observer.observe(target)
        return () => observer.disconnect()
    }, [fetchNextPage, hasNextPage, isFetchingNextPage])

    return (
        <>
            <Posts posts={posts} />
            <div ref={loadMoreRef} className='h-2 w-full' />

            {isFetchingNextPage && (
                <p className='mt-4 text-center text-sm text-gray-500 dark:text-gray-400'>Đang tải thêm bài viết...</p>
            )}
            {isError && <p className='mt-4 text-center text-sm text-red-500'>Không thể tải thêm bài viết.</p>}
        </>
    )
}

export default HomePostsInfinite
